import { useEffect, useState } from 'react';
import { X, CircleDot, Flag, Trash2, GitFork, Square } from 'lucide-react';
import clsx from 'clsx';
import {
  CATEGORY_LABELS,
  STATE_CATEGORIES,
  slugify,
  type WorkflowState,
} from './workflowModel';

interface Props {
  state: WorkflowState;
  /** Ids already taken by other states, so a rename cannot collide. */
  takenIds: string[];
  itemCount: number;
  readOnly?: boolean;
  onSave: (updated: WorkflowState) => void;
  onDelete: () => void;
  onClose: () => void;
}

const INPUT =
  'w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-60';
const LABEL = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1';

/**
 * Properties for a state selected on the canvas.
 *
 * Carries exactly the fields the table row does — there is one set of property
 * semantics, presented two ways, rather than two editors that can drift.
 * Edits are held locally and applied on save so Escape can genuinely cancel.
 */
export default function StatePropertiesModal({
  state, takenIds, itemCount, readOnly = false, onSave, onDelete, onClose,
}: Props) {
  const [draft, setDraft] = useState<WorkflowState>(state);

  useEffect(() => setDraft(state), [state]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const patch = (updates: Partial<WorkflowState>) =>
    setDraft((prev) => ({ ...prev, ...updates }));

  const isDecision = draft.kind === 'decision';

  /** Switching kind clears whatever the other kind cannot carry. */
  const setKind = (kind: 'state' | 'decision') =>
    setDraft((prev) => ({
      ...prev,
      kind,
      ...(kind === 'decision'
        ? { wipLimit: 0, slaHours: 0, isInitial: false, isTerminal: false,
            requiredFields: [], color: '#f59e0b' }
        : {}),
    }));

  const idCollision = draft.id !== state.id && takenIds.includes(draft.id);
  const idMissing = !draft.id.trim();
  const labelMissing = !draft.label.trim();
  const blocked = idCollision || idMissing || labelMissing;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Properties for ${state.label}`}
    >
      <div
        className="w-full max-w-md rounded-xl bg-white dark:bg-gray-800 shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="h-1.5"
          style={{ backgroundColor: draft.color || '#94a3b8' }}
        />

        <div className="flex items-center justify-between px-4 h-12 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
            {draft.label || 'State'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-3 max-h-[65vh] overflow-y-auto">
          {/* Kind first: it decides which of the fields below even apply. */}
          <div>
            <label className={LABEL}>Node type</label>
            <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden w-fit">
              {([['state', 'State', Square], ['decision', 'Decision', GitFork]] as const).map(
                ([kind, label, Icon]) => (
                  <button
                    key={kind}
                    disabled={readOnly}
                    onClick={() => setKind(kind)}
                    aria-pressed={draft.kind === kind || (kind === 'state' && !draft.kind)}
                    className={clsx(
                      'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium disabled:opacity-50',
                      (draft.kind || 'state') === kind
                        ? 'bg-accent text-white'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    )}
                  >
                    <Icon size={12} /> {label}
                  </button>
                )
              )}
            </div>
            <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
              {isDecision
                ? 'Items are routed through a decision, never parked on it — so it carries no category, WIP limit or SLA.'
                : 'Somewhere an item can rest. Appears as a board column.'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Label</label>
              <input
                autoFocus
                value={draft.label}
                disabled={readOnly}
                onChange={(e) => {
                  const label = e.target.value;
                  const derived = slugify(draft.label);
                  const keepInStep =
                    draft.id === derived || draft.id.startsWith('state');
                  patch({
                    label,
                    ...(keepInStep && !itemCount ? { id: slugify(label) } : {}),
                  });
                }}
                className={INPUT}
              />
              {labelMissing && (
                <p className="mt-1 text-[11px] text-red-500">A label is required.</p>
              )}
            </div>
            <div>
              <label className={LABEL}>Id</label>
              <input
                value={draft.id}
                disabled={readOnly}
                onChange={(e) => patch({ id: slugify(e.target.value) })}
                className={`${INPUT} font-mono text-xs`}
              />
              {idMissing && (
                <p className="mt-1 text-[11px] text-red-500">An id is required.</p>
              )}
              {idCollision && (
                <p className="mt-1 text-[11px] text-red-500">
                  Another state already uses this id.
                </p>
              )}
              {!idCollision && !idMissing && draft.id !== state.id && itemCount > 0 && (
                <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-500">
                  {itemCount} item{itemCount === 1 ? '' : 's'} sit in this state and
                  will be stranded by the rename.
                </p>
              )}
            </div>
          </div>

          {!isDecision && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Category</label>
              <select
                value={draft.category}
                disabled={readOnly}
                onChange={(e) => patch({ category: e.target.value })}
                className={INPUT}
              >
                {STATE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                ))}
              </select>
              <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
                What counts as finished, independently of the name.
              </p>
            </div>
            <div>
              <label className={LABEL}>Colour</label>
              <input
                type="color"
                value={draft.color}
                disabled={readOnly}
                onChange={(e) => patch({ color: e.target.value })}
                className="w-full h-9 rounded border border-gray-300 dark:border-gray-600 bg-transparent"
              />
            </div>
          </div>
          )}

          {!isDecision && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>WIP limit</label>
              <input
                type="number"
                min={0}
                value={draft.wipLimit}
                disabled={readOnly}
                onChange={(e) => patch({ wipLimit: Number(e.target.value) || 0 })}
                className={INPUT}
              />
              <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">0 = unlimited</p>
            </div>
            <div>
              <label className={LABEL}>SLA hours</label>
              <input
                type="number"
                min={0}
                value={draft.slaHours}
                disabled={readOnly}
                onChange={(e) => patch({ slaHours: Number(e.target.value) || 0 })}
                className={INPUT}
              />
              <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">0 = no SLA</p>
            </div>
          </div>
          )}

          {!isDecision && (
          <div>
            <label className={LABEL}>Fields required to enter</label>
            <input
              value={draft.requiredFields.join(', ')}
              disabled={readOnly}
              onChange={(e) =>
                patch({
                  requiredFields: e.target.value
                    .split(',')
                    .map((f) => f.trim())
                    .filter(Boolean),
                })
              }
              placeholder="e.g. assignee, dueDate"
              className={INPUT}
            />
          </div>
          )}

          {!isDecision && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => patch({ isInitial: !draft.isInitial })}
              disabled={readOnly}
              aria-pressed={draft.isInitial}
              className={clsx(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-50',
                draft.isInitial
                  ? 'bg-accent/10 border-accent text-accent'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-accent'
              )}
            >
              <CircleDot size={13} /> Start state
            </button>
            <button
              onClick={() => patch({ isTerminal: !draft.isTerminal })}
              disabled={readOnly}
              aria-pressed={draft.isTerminal}
              className={clsx(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-50',
                draft.isTerminal
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-500 text-green-700 dark:text-green-400'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-green-500'
              )}
            >
              <Flag size={13} /> End state
            </button>
          </div>
          )}

          {isDecision && (
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              Give this decision its branches in the Transitions section: each one
              gets a condition, and exactly one must be marked <strong>default</strong>
              so an item matching nothing still has somewhere to go.
            </p>
          )}
          {draft.isInitial && !state.isInitial && (
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              Marking this as the start state clears it from whichever state holds
              it now — there can only be one.
            </p>
          )}
        </div>

        {!readOnly && (
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onDelete}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-red-400 hover:text-red-500"
            >
              <Trash2 size={13} /> Remove state
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => onSave(draft)}
                disabled={blocked}
                title={blocked ? 'Fix the highlighted fields first' : undefined}
                className="px-3 py-1.5 text-sm font-medium rounded-lg bg-accent text-white hover:bg-accent/90 disabled:opacity-40"
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
